// src/store/slices/sales/quotationSlice.ts

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { QuotationFormValues } from "../../../types/sale/quotation/quotation";


// =====================================================
// 📄 Initial Form
// =====================================================

const createInitialQuotationForm = (): QuotationFormValues => {

    const date = new Date();

    date.setDate(date.getDate() + 15);

    return {

        id: null,

        customer: null,

        seller: null,

        branch: null,

        expirationDate: date.toISOString().split('T')[0],

        notes: "",

        paymentCondition: null,

        itemsDiscountTotal: 0,

        globalDiscountPercent: 0,

        globalDiscountAmount: 0,

        total: 0,

        items: []

    };
};


// =====================================================
// 📄 State
// =====================================================

interface QuotationState {

    form: QuotationFormValues;

    activeBranchId: number | null;

    quotations: any[];

    pendingQuotations: any[];

    quotationHistory: any[];

    selectedQuotation: any | null;

    selectedProduct: any | null;

    loading: boolean;

    error: string | null;

    fieldErrors: Record<string, string>;

    filters: {

        search: string;

        status: string | null;

    };

    // =====================================================
    // 🔹 Modal
    // =====================================================

    showQuotationModal: boolean;

    showProcessModal: boolean;

}

// =====================================================
// 📄 Initial State
// =====================================================

const initialState: QuotationState = {

    form: {
        ...createInitialQuotationForm(),
    },

    activeBranchId: null,

    quotations: [],

    pendingQuotations: [],

    quotationHistory: [],

    selectedQuotation: null,

    selectedProduct: null,

    loading: false,

    error: null,

    fieldErrors: {},

    filters: {

        search: "",

        status: null

    },

    // =====================================================
    // 🔹 Modal
    // =====================================================

    showQuotationModal: false,

    showProcessModal: false,

};

// =====================================================
// 📄 Slice
// =====================================================

const quotationSlice = createSlice({

    name: "quotation",

    initialState,

    reducers: {


        setActiveBranchId(
            state,
            action: PayloadAction<number | null>
        ) {

            state.activeBranchId =
                action.payload;

        },

        // =====================================================
        // 🔹 Form
        // =====================================================

        setQuotationForm(
            state,
            action: PayloadAction<QuotationFormValues>
        ) {

            state.form = action.payload;

        },

        updateQuotationField(
            state,
            action: PayloadAction<{
                field: keyof QuotationFormValues;
                value: any;
            }>
        ) {

            const { field, value } = action.payload;

            state.form = {

                ...state.form,

                [field]: value

            };

        },


        // =====================================================
        // 🔹 Clear quotations
        // =====================================================

        clearQuotations(state) {

            state.quotations = [];

        },

        clearPendingQuotations(state) {

            state.pendingQuotations = [];

        },

        clearQuotationHistory(state) {

            state.quotationHistory = [];

        },

        clearQuotationForm(state) {

            state.form = {
                ...createInitialQuotationForm()
            };

        },

        // =====================================================
        // 🔹 Loading
        // =====================================================

        setLoading(
            state,
            action: PayloadAction<boolean>
        ) {

            state.loading = action.payload;

        },

        setError(
            state,
            action: PayloadAction<string | null>
        ) {

            state.error = action.payload;

        },

        // =====================================================
        // 🔹 Quotations
        // =====================================================

        setQuotations(
            state,
            action: PayloadAction<any[]>
        ) {

            state.quotations = action.payload;

        },

        setPendingQuotations(
            state,
            action: PayloadAction<any[]>
        ) {

            state.pendingQuotations =
                action.payload;

        },

        setQuotationHistory(
            state,
            action: PayloadAction<any[]>
        ) {

            state.quotationHistory =
                action.payload;

        },

        setSelectedQuotation(
            state,
            action: PayloadAction<any | null>
        ) {

            state.selectedQuotation = action.payload;

        },

        clearSelectedQuotation(state) {

            state.selectedQuotation = null;

        },

        setSelectedProduct(
            state,
            action: PayloadAction<any | null>
        ) {

            state.selectedProduct =
                action.payload;

        },

        updateSelectedProductField(
            state,
            action: PayloadAction<any>
        ) {

            if (!state.selectedProduct) {
                return;
            }

            state.selectedProduct = {

                ...state.selectedProduct,

                ...action.payload

            };

        },

        clearSelectedProduct(state) {

            state.selectedProduct = null;

        },


        updateQuotationCalculation(
            state,
            action: PayloadAction<any>
        ) {

            state.form = {

                ...state.form,

                // ==========================
                // 🔹 Discounts
                // ==========================

                itemsDiscountTotal:
                    action.payload.itemsDiscountTotal,

                globalDiscountPercent:
                    action.payload.globalDiscountPercent,

                globalDiscountAmount:
                    action.payload.globalDiscountAmount,

                // ==========================
                // 🔹 Totals
                // ==========================

                total: action.payload.total,

                // ==========================
                // 🔹 Items
                // ==========================

                items: action.payload.items

            };

        },

        // =====================================================
        // 🔹 Filters
        // =====================================================

        setSearchFilter(
            state,
            action: PayloadAction<string>
        ) {

            state.filters.search = action.payload;

        },

        setStatusFilter(
            state,
            action: PayloadAction<string | null>
        ) {

            state.filters.status = action.payload;

        },

        resetFilters(state) {

            state.filters = {

                search: "",

                status: null

            };

        },

        resetQuotationFormCompletely(state) {

            state.form = {
                ...createInitialQuotationForm()
            };

            state.activeBranchId = null;

            state.selectedQuotation = null;

            state.selectedProduct = null;

            state.loading = false;

            state.error = null;

            state.fieldErrors = {};

            state.filters = {
                search: '',
                status: null
            };

        },

        // =====================================================
        // 🔹 Modal
        // =====================================================

        openQuotationModal(state) {

            state.showQuotationModal = true;

        },

        closeQuotationModal(state) {

            state.showQuotationModal = false;

        },

        // =====================================================
        // 🔹 Process Modal
        // =====================================================

        openProcessModal(state) {

            state.showProcessModal = true;

        },

        closeProcessModal(state) {

            state.showProcessModal = false;

        },

        removePendingQuotationById(
            state,
            action: PayloadAction<number>
        ) {

            state.pendingQuotations =
                state.pendingQuotations.filter(
                    quotation =>
                        quotation.id !== action.payload
                );

        },

        removeQuotationHistoryById(
            state,
            action: PayloadAction<number>
        ) {

            state.quotationHistory =
                state.quotationHistory.filter(
                    quotation =>
                        quotation.id !== action.payload
                );

        },

        clearQuotationState(state) {

            state.form = {
                ...createInitialQuotationForm()
            };

            state.activeBranchId = null;

            state.quotations = [];

            state.pendingQuotations = [];

            state.quotationHistory = [];

            state.selectedQuotation = null;

            state.selectedProduct = null;

            state.loading = false;

            state.error = null;

            state.fieldErrors = {};

            state.filters = {
                search: '',
                status: null
            };

            state.showQuotationModal = false;

            state.showProcessModal = false;

        },

        setFieldErrors(
            state,
            action: PayloadAction<Record<string, string>>
        ) {

            state.fieldErrors = action.payload;

        },

        clearFieldErrors(state) {

            state.fieldErrors = {};

        },

    }

});

// =====================================================
// 📤 Exports
// =====================================================

export const {

    setActiveBranchId,
    setQuotationForm,
    updateQuotationField,
    clearQuotations,
    clearPendingQuotations,
    clearQuotationHistory,
    clearQuotationForm,
    setLoading,
    setError,
    setQuotations,
    setPendingQuotations,
    setQuotationHistory,
    setSelectedQuotation,
    clearSelectedQuotation,
    setSelectedProduct,
    updateSelectedProductField,
    clearSelectedProduct,
    updateQuotationCalculation,
    setSearchFilter,
    setStatusFilter,
    resetFilters,
    resetQuotationFormCompletely,
    openQuotationModal,
    closeQuotationModal,
    openProcessModal,
    closeProcessModal,
    removePendingQuotationById,
    removeQuotationHistoryById,
    clearQuotationState,
    setFieldErrors,
    clearFieldErrors,
} = quotationSlice.actions;

export default quotationSlice.reducer;